import React, { Component, useContext, useEffect, useState } from 'react';
import { Tabs, Form, Input, Button, notification, message, AutoComplete, Spin, Breadcrumb } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { addAccountObject, addCompanyCode, addSubsidiary, fetchAccountObjectsData } from '../../../core/services/administration.service'; 
import { AccountObject, CompanyCodeObject, Subsidiary } from '../../../core/interfaces/Administration/AccountObject';
import { appInsights } from '../../../core/services/insight.service'; // Import the insight service
import { UserContext } from '../../../context/user-context';
import GlobalErrorMessage from '../../global-error/GlobalErrorMessage';

const { TabPane } = Tabs;

const ManageApplication: React.FC = () => {
  const user = useContext(UserContext);
  const [activeTab, setActiveTab] = useState<string>('1');
  const [formaccobj] = Form.useForm<FormInstance<any>>();
  const [formsubsidiary] = Form.useForm<FormInstance<any>>();
  const [formcompanycode] = Form.useForm<FormInstance<any>>();
  const [accountObjects, setAccountObjects] = useState<AccountObject[]>([]);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<{ key: string, value: string }[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<{ key: string, value: string }[]>([]);
  const [selectedParentObjectId, setSelectedParentObjectId] = useState<number>(0);
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [apiError, setApiError] = useState(null);
  const userProfile=useContext(UserContext);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === '3') {
      fetchAccountObjects();
    }
  };

  const handleSelect = (value: string) => {
    const firstNumber = value.split('-')[0].trim();
    setSelectedValue(firstNumber);
    setSelectedParentObjectId(Number(firstNumber));
  };

  const handleChange = (value: string) => {
    setSelectedValue(value);
  };

  const handleSubmit = async (tab: any) => {
    try {
      let isSuccess = false;
      switch (activeTab) {
        case '2':
            try {
              const values: any = formaccobj.getFieldsValue();
                const data: AccountObject = {
                  objectId: 0,
                  object: values.newObject,
                  objDescription: values.newObjectDesc
                };
                const response = await addAccountObject(data,userProfile);
                if (response) {
                  message.success('AccountObject added successfully');
                  fetchAccountObjects(); // Refresh the data
                  isSuccess = true;
                } else {
                  // Handle API failure (e.g., response.success is false)
                  message.error('Failed to add AccountObject');
                }
              } catch (error) {
                setApiError(error);
                // Handle exceptions (e.g., network errors)
                message.error('Failed to add AccountObject');
              }
              break;
        case '3':
            try {
              const values: any = formsubsidiary.getFieldsValue();
                const data: Subsidiary = {
                    parentObject: selectedParentObjectId,
                    subsidiary: values.newSubsidiary,
                    subDescription: values.newSubsidiaryDesc
                };
                const response = await addSubsidiary(data,userProfile);
                message.success('Subsidiary added successfully');
                isSuccess = true; 
    } catch (error) {
      setApiError(error);
      message.error('Failed to add Subsidiary');
    }
          break;
        case '4':
            try {
              const values: any = formcompanycode.getFieldsValue();
                const data: CompanyCodeObject = {
                  CCN: values.newCompany,
                  CompanyName: values.newCompanyName,
                  GlobalLocationCode: values.newCompanyLoCode,
                  SecondTierCutback: values.newCompany2ndTierCutback
                };
                const response = await addCompanyCode(data,userProfile);
                isSuccess = true; 
                message.success('CompanyCode added successfully');
    } catch (error) {
      setApiError(error);
      message.error('Failed to add companycode');
    }
              break;
        default:
          break;
      }
      if (isSuccess) {
        notification.success({
          message: 'Success',
          description: 'The operation was successful.',
        });
      } else {
        notification.error({
          message: 'Failed',
          description: 'There was an error processing your request.',
        });
      }
    } catch (error) {
      setApiError(error);
      notification.error({
        message: 'Failed',
        description: 'There was an error processing your request.',
      });
      appInsights.trackException({ exception: error as Error,  properties: { Component:'ManageApplication', method:'handleSubmit', context: 'Handle Submit', userId: user.email } }); // Track exception
    }
  };

  const handleError = (errorInfo: any) => {
    notification.error({
      message: 'Failed',
      description: 'The input fields are not properly filled. Check the form and submit again.',
    });
  };

  const handleSearch = (value: string) => {
    setFilteredOptions(
      autoCompleteOptions.filter(option =>
        option.value.toUpperCase().includes(value.toUpperCase())
      ).map((option, index) => ({ key: `${option.key}-${index}`, value: option.value }))
    );
  };

  useEffect(() => {
    fetchAccountObjects();
  }, []);

  const fetchAccountObjects = async () => {
    try {
      const response = await fetchAccountObjectsData(userProfile);
      setAccountObjects(response);
      const options = response.map((item, index) => ({
        value: `${item.object} - ${item.objDescription}`,
        key: `${item.objectId}-${index}`
      }));
      setFilteredOptions(options);
      const filteredoptions = response.map((item, index) => ({
        value: `${item.object} - ${item.objDescription}`,
        key: `${item.objectId}-${index}`
      }));
      setAutoCompleteOptions(filteredoptions);
    } catch (error) {
      setApiError(error);
      message.error('Failed to fetch account objects');
      appInsights.trackException({ exception: error as Error, properties: { context: 'Fetching Account Objects' } }); // Track exception
    }
  };
  const handleRetry = () => {
    setApiError(null);
  };
  return (
    <>
     <Breadcrumb
       items={[
         {
           title: "administration/manage-application",
         },
       ]}
     />
       {apiError ? (
         <GlobalErrorMessage 
         error={apiError}
         visible={!!apiError}
         onClose={() => setApiError(null)}
         onRetry={handleRetry}
       />
       ) : (
    <div style={{ padding: '20px' }}>
      <h3>Manage Application</h3>
      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        <TabPane tab="Menu" key="1">
          <ul>
            <li><a onClick={() => setActiveTab('2')}>Manage Account Objects</a></li>
            <li><a onClick={() => setActiveTab('3')}>Manage Account Subsidiaries</a></li>
            <li><a onClick={() => setActiveTab('4')}>Manage Company Codes</a></li>
          </ul>
        </TabPane>
        <TabPane tab="Manage Account Objects" key="2">
          <div className='div-container'>
          <Form form={formaccobj} onFinish={()=>handleSubmit("2")} onFinishFailed={handleError}>
            <Form.Item name="newObject" label="Object" rules={[{ required: true, len: 6 }]}>
              <Input placeholder="Eg. 122345" />
            </Form.Item>
            <Form.Item name="newObjectDesc" label="Object Description" rules={[{ required: true }]}>
              <Input placeholder="Eg. Lunch Money" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">Submit</Button>
            </Form.Item>
          </Form></div>
        </TabPane>
        <TabPane tab="Manage Account Subsidiaries" key="3">
          <div className='div-container'>
          <Form form={formsubsidiary} onFinish={()=>handleSubmit("3")} onFinishFailed={handleError}>
            <Form.Item name="newSubParentObject" label="Parent Object" rules={[{ required: true }]} >
            <AutoComplete
                onSearch={handleSearch}
                onSelect={handleSelect}
                onChange={handleChange}
                value={selectedValue}
                placeholder="Eg. 122345 Hint: You can search by description!"
              >
                {filteredOptions.map(item => {
                  const parts = item.value.split('-');
                  const objectId = parts[0].trim();
                  const description = parts.length > 1 ? parts[1].trim() : '';
                 
                  return (
                    <AutoComplete.Option key={item.key} value={objectId}>
                    {objectId} - {description}
                    </AutoComplete.Option>
                  );
                })}
            </AutoComplete>
            </Form.Item>
            <Form.Item name="newSubsidiary" label="Subsidiary" rules={[{ required: true }]}>
              <Input placeholder="Eg. 290N00" maxLength={8} />
            </Form.Item>
            <Form.Item name="newSubsidiaryDesc" label="Subsidiary Description" rules={[{ required: true }]}>
              <Input placeholder="Eg. Community Chest" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">Submit</Button>
            </Form.Item>
          </Form>
          </div>
        </TabPane>
        <TabPane tab="Manage Company Codes" key="4">
          <div className='div-container'>
          <Form form={formcompanycode} onFinish={()=>handleSubmit("4")} onFinishFailed={handleError}>
            <Form.Item name="newCompany" label="Company Code" rules={[{ required: true, len: 5 }]}>
              <Input placeholder="Eg. 00379" />
            </Form.Item>
            <Form.Item name="newCompanyName" label="Company Name" rules={[{ required: true }]}>
              <Input placeholder="Eg. Offshore Intergalactic Drilling" />
            </Form.Item>
            <Form.Item name="newCompanyLoCode" label="Company Global Location Code" rules={[{ required: true, len: 3 }]}>
              <Input placeholder="Eg. 391" />
            </Form.Item>
            <Form.Item name="newCompany2ndTierCutback" label="Company Global Second Tier Cutback" rules={[{ required: true, len: 1 }]}>
              <Input placeholder="Eg. 9" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">Submit</Button>
            </Form.Item>
          </Form></div>
        </TabPane>
      </Tabs>
    </div>)}
    </>
  );
};

export default ManageApplication;